import { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import Modal from "../../../components/base/Modal";
import { Badge, Button, Spinner, notifyError, notifySuccess } from "../../../components/ui";
import { dialogTheme } from "../../../components/ui/dialog";
import NewShipmentModal from "../../envios/components/NewShipmentModal";
import { useCreateShipment } from "../../../features/shipments/mutations";
import { useEvidence, useOperation, useRadarMutation } from "../hooks/useRadarCorreo";
import { radarCorreoApi, apiErrorMessage, type Operation } from "../services/radarCorreoService";
import { OP_STATUS_TONE, STAGE_TONE, fmtDate, fmtMoney, laneLabel, primaryRef } from "../lib/format";

const MODE_TO_CARRIER: Record<string, "ground" | "air" | "maritime"> = { road: "ground", courier: "ground", rail: "ground", air: "air", ocean: "maritime" };

export function OperationDetailModal({ operationId, onClose }: { operationId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation("radarCorreo");
  const lang = i18n.language;
  const detail = useOperation(operationId);
  const [showEvidence, setShowEvidence] = useState(false);
  const [creating, setCreating] = useState(false);
  const evidence = useEvidence(operationId, showEvidence);
  const act = useRadarMutation(async (fn: () => Promise<unknown>) => fn());
  const createShipment = useCreateShipment();

  const run = (fn: () => Promise<unknown>, ok?: string) =>
    act.mutate(fn, {
      onSuccess: () => ok && void notifySuccess(ok),
      onError: (err) => void notifyError(t("common.error"), apiErrorMessage(err) || undefined),
    });

  const askText = async (title: string) => {
    const r = await Swal.fire({ ...dialogTheme("primary"), title, input: "text", showCancelButton: true, cancelButtonText: t("common.cancel"), confirmButtonText: t("common.save") });
    return r.isConfirmed ? String(r.value || "").trim() : "";
  };

  const op: Operation | undefined = detail.data?.operation;
  const content = !op ? (
    <div className="flex justify-center p-10">
      <Spinner size="lg" />
    </div>
  ) : (
    <div className="space-y-5 p-4 sm:p-6" data-testid="radar-operation-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={OP_STATUS_TONE[op.status]}>{t(`operations.status.${op.status}`)}</Badge>
        <Badge tone={STAGE_TONE[op.stage]}>{t(`operations.stage.${op.stage}`)}</Badge>
        <Badge tone="neutral">{t(`operations.mode.${op.mode || "unknown"}`, { defaultValue: op.mode })}</Badge>
        <Badge tone="neutral" variant="outline">{t(`operations.confidence.${op.confidence || "low"}`)}</Badge>
        {op.conflict && <Badge tone="warning">{t("operations.conflict")}</Badge>}
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-content-secondary">{t("operations.columns.lane")}</dt>
          <dd className="text-content">{laneLabel(op)}</dd>
        </div>
        <div>
          <dt className="text-xs text-content-secondary">{t("operations.detail.firstEta")}</dt>
          <dd className="text-content">{fmtDate(op.firstEtaAt, lang)}</dd>
        </div>
        <div>
          <dt className="text-xs text-content-secondary">{t("operations.detail.currentEta")}</dt>
          <dd className="text-content">{fmtDate(op.etaAt, lang)}</dd>
        </div>
        <div>
          <dt className="text-xs text-content-secondary">{t("operations.detail.delivered")}</dt>
          <dd className="text-content">{fmtDate(op.deliveredAt, lang)}</dd>
        </div>
      </dl>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-content">{t("operations.detail.references")}</h3>
        <div className="flex flex-wrap gap-1.5">
          {(Object.entries(op.refs || {}) as [string, string[] | undefined][]).flatMap(([type, values]) =>
            (values || []).map((v: string) => (
              <Badge key={`${type}:${v}`} tone="primary" variant="outline">
                {type.toUpperCase()} {v}
              </Badge>
            )),
          )}
          {!primaryRef(op) && <span className="text-xs text-content-secondary">{t("operations.noReference")}</span>}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-content">{t("operations.detail.counterparties")}</h3>
        <div className="flex flex-wrap gap-1.5">
          {(detail.data?.counterparties || []).map((c) => (
            <Badge key={c.id} tone="neutral">
              {c.displayName} · {c.roles.map((r) => t(`counterparties.roles.${r}`)).join(", ")}
            </Badge>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-content">{t("operations.detail.timeline")}</h3>
        <ol className="space-y-2 border-l border-line pl-4">
          {(detail.data?.events || []).map((e) => (
            <li key={e.id} className="text-sm">
              <span className="font-medium text-content">{t(`events.${e.type}`, { defaultValue: e.type })}</span>
              <span className="text-content-secondary"> · {fmtDate(e.occurredAt, lang)}</span>
              {e.data?.etaNew ? <span className="text-content-secondary"> · ETA {fmtDate(e.data.etaNew, lang)}</span> : null}
              {e.data?.documentType ? <span className="text-content-secondary"> · {e.data.documentType}</span> : null}
            </li>
          ))}
        </ol>
      </section>

      {(detail.data?.finance || []).length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-content">{t("operations.detail.finance")}</h3>
          <ul className="space-y-1 text-sm">
            {detail.data!.finance.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-2">
                <span className="text-content">{t(`finance.kind.${f.kind}`)}</span>
                <span className="font-semibold tabular-nums text-content">{fmtMoney(f.amount, f.currency, lang)}</span>
                <Badge tone="neutral" size="sm">{t(`finance.direction.${f.direction}`)}</Badge>
                <Badge tone={f.status === "paid" ? "success" : f.status === "overdue" ? "error" : "neutral"} size="sm">
                  {t(`finance.status.${f.status}`, { defaultValue: f.status })}
                </Badge>
                {f.dueDate ? <span className="text-xs text-content-secondary">{t("finance.due", { date: fmtDate(f.dueDate, lang) })}</span> : null}
                <span className="text-xs text-content-faint">{t(`finance.source.${f.source}`)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg bg-subtle p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-content">{t("operations.detail.evidence")}</h3>
          {!showEvidence && (
            <Button size="xs" variant="secondary" icon={<i className="ri-lock-line" />} onClick={() => setShowEvidence(true)}>
              {t("operations.actions.evidence")}
            </Button>
          )}
        </div>
        <p className="text-xs text-content-secondary">{t("operations.detail.evidenceHint")}</p>
        {showEvidence &&
          (evidence.isLoading ? (
            <Spinner size="sm" />
          ) : (evidence.data || []).length === 0 ? (
            <p className="mt-2 text-xs text-content-secondary">{t("operations.detail.noEvidence")}</p>
          ) : (
            <ul className="mt-2 space-y-2" data-testid="radar-evidence">
              {evidence.data!.map((m) => (
                <li key={m.id} className="rounded-md bg-card p-2 text-xs">
                  <p className="font-semibold text-content">{m.subject}</p>
                  <p className="text-content-secondary">
                    {fmtDate(m.date, lang)} · {m.fromName || ""} {m.note ? `· ${m.note}` : ""}
                  </p>
                  <p className="mt-1 whitespace-pre-line text-content-secondary">{m.excerpt}</p>
                </li>
              ))}
            </ul>
          ))}
      </section>

      {op.suggestedShipmentId && !op.shipmentId && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-info/10 p-3 text-sm text-info">
          <span className="flex-1">{t("operations.suggested")}</span>
          <Button size="sm" variant="info" onClick={() => run(() => radarCorreoApi.linkShipment(op.id, op.suggestedShipmentId as string), t("operations.linked", { id: op.suggestedShipmentId }))}>
            {t("operations.actions.link")}
          </Button>
        </div>
      )}
      {op.shipmentId && <p className="text-sm text-success">{t("operations.linked", { id: op.shipmentId })}</p>}

      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        {op.status === "proposed" && (
          <Button variant="successSoft" icon={<i className="ri-check-line" />} onClick={() => run(() => radarCorreoApi.confirm(op.id))} data-testid="radar-op-confirm">
            {t("operations.actions.confirm")}
          </Button>
        )}
        {!op.shipmentId ? (
          <>
            <Button variant="secondary" icon={<i className="ri-truck-line" />} onClick={() => setCreating(true)}>
              {t("operations.actions.createShipment")}
            </Button>
            <Button
              variant="secondary"
              icon={<i className="ri-links-line" />}
              onClick={async () => {
                const id = await askText(t("operations.linkPrompt"));
                if (id) run(() => radarCorreoApi.linkShipment(op.id, id), t("operations.linked", { id }));
              }}
            >
              {t("operations.actions.link")}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => run(() => radarCorreoApi.unlinkShipment(op.id))}>
            {t("operations.actions.unlink")}
          </Button>
        )}
        <Button
          variant="ghost"
          icon={<i className="ri-git-merge-line" />}
          onClick={async () => {
            const ids = (await askText(t("operations.mergePrompt"))).split(",").map((s) => s.trim()).filter(Boolean);
            if (ids.length) run(() => radarCorreoApi.merge(op.id, ids));
          }}
        >
          {t("operations.actions.merge")}
        </Button>
        {op.status !== "dismissed" && (
          <Button variant="dangerSoft" icon={<i className="ri-close-line" />} onClick={() => run(() => radarCorreoApi.dismiss(op.id))}>
            {t("operations.actions.dismiss")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Modal title={op ? primaryRef(op) || t("operations.noReference") : t("common.loading")} onClose={onClose} content={content} width="max-w-3xl w-full" disableCRUD />
      {op && (
        <NewShipmentModal
          open={creating}
          onClose={() => setCreating(false)}
          initial={{
            code: op.refs?.tracking?.[0] || op.refs?.bl?.[0] || op.refs?.awb?.[0] || op.refs?.container?.[0],
            shipmentName: [primaryRef(op), laneLabel(op) !== "—" ? laneLabel(op) : null].filter(Boolean).join(" · "),
            carrierType: MODE_TO_CARRIER[op.mode],
            note: t("appName"),
          }}
          onSubmit={async (data) => {
            const created = await createShipment.mutateAsync(data);
            const id = (created as { id?: string } | undefined)?.id;
            if (id) await radarCorreoApi.linkShipment(op.id, id);
            setCreating(false);
            void detail.refetch();
            void notifySuccess(t("operations.linked", { id: id || "" }));
          }}
        />
      )}
    </>
  );
}
